import { useState } from 'react'
import { useRouter } from 'next/router'
import { AxiosError } from 'axios'
import { checkIsServiceNonTopUp, checkIsWithPercentFee, PaymentPayee, useCyberStore } from '@entities/tsp'
import { authHelper, useAuthDataStore } from '@entities/user'
import { CYBER_PAY_URL_RESULT, reqHeaders } from '@shared/config'
import { ewalletApi } from '@root/api/api'
import { ConfirmTransactionByOtpRq, TransactionResponse } from '@root/api/ump-ewallet'
import { generatePath } from '@shared/lib'
import { baseApi } from '@shared/config/environments/production'

interface UseConfirmCodeArgs {
  showVouchersForm: boolean
  payee: PaymentPayee
  clearResendCodeError: () => void
}

export const useConfirmCode = ({ showVouchersForm, payee, clearResendCodeError }: UseConfirmCodeArgs) => {
  const [isConfirmCodeLoading, setIsConfirmCodeLoading] = useState<boolean>(false)
  const [confirmCodeError, setConfirmCodeError] = useState<string | null>(null)

  const router = useRouter()

  const { profile } = useAuthDataStore()
  const { pcekMdOrder } = useCyberStore()

  const url = router.query.id as string

  const userId = profile?.userId

  const zone = authHelper.isAuthenticated() ? 'b' : 'anonymous'

  const onSuccess = () => {
    const isNonTopUpService = checkIsServiceNonTopUp(showVouchersForm, payee)
    const isTopUpWithPercentFeeService = checkIsWithPercentFee(payee)

    const generatedPath = `${generatePath(CYBER_PAY_URL_RESULT, {
      mdOrder: pcekMdOrder,
    })}?url=${url}&voucher=${isNonTopUpService}&topup=${isTopUpWithPercentFeeService}`

    router.push(`${baseApi}/${generatedPath}`)
  }

  const onError = (data: TransactionResponse) => {
    if (data?.errorCode && data?.errorCode !== 106) {
      onSuccess()
    }
  }

  const handleConfirmCode = (otp: string, showErr: (text: string) => void) => {
    const params: ConfirmTransactionByOtpRq = {
      userId,
      mdOrder: pcekMdOrder,
      otp,
    }

    setIsConfirmCodeLoading(true)
    setConfirmCodeError(null)
    clearResendCodeError()

    ewalletApi
      .confirmTransactionByOtp(zone, params, {
        method: 'POST',
        headers: { ...reqHeaders },
        body: JSON.stringify(params),
      })
      .then(({ data }) => {
        if (!data?.errorCode) {
          onSuccess?.()
        } else {
          showErr(data?.errorMessage as string)
          onError?.(data)
        }
      })
      .catch((error: AxiosError) => {
        const message = error?.message

        setConfirmCodeError(message)

        showErr(message)
      })
      .finally(() => setIsConfirmCodeLoading(false))
  }

  return {
    isConfirmCodeLoading,
    setIsConfirmCodeLoading,
    confirmCodeError,
    setConfirmCodeError,
    handleConfirmCode,
  }
}
